import type { ReactNode } from 'react'

/**
 * Les entrées de navigation — 06 §5.
 *
 * UNE seule liste alimente le rail de bureau ET la barre d'onglets mobile,
 * comme chez Twitter : la barre du bas n'est pas un autre menu, c'est le même
 * menu réduit. Deux listes divergeraient à la première entrée ajoutée.
 *
 * La version 2022 avait quatre entrées principales (accueil, quiz, cours,
 * notifications) et un menu secondaire en dropdown. On garde cette répartition
 * — elle était juste — en l'alignant sur le nouveau périmètre : le réseau
 * social disparaît, les archives d'examens et le classement arrivent.
 */
export interface NavEntry {
  id: string
  label: string
  /** Vrai pour les cinq entrées que la barre du bas peut accueillir. */
  primary: boolean
  icon: (filled: boolean) => ReactNode
}

/**
 * L'icône d'une entrée — UN silhouette, deux états.
 *
 * **Un seul tracé par icône, et c'est le correctif.** Chaque icône portait
 * auparavant DEUX chemins écrits à la main, l'un plein et l'autre creux. Deux
 * dessins à tenir d'accord pour une même forme : ils divergeaient au premier
 * retouche, et surtout ils étaient construits d'angles vifs — segments joints
 * à 90°, pointes sèches. Ici la silhouette est unique ; l'état actif la
 * REMPLIT, l'état au repos la trace.
 *
 * **`strokeLinejoin="round"` est ce qui arrondit tout, d'un coup.** Chaque
 * jonction de segments se raccorde par un quart de rond du rayon de la plume,
 * sans qu'aucun chemin ait à l'écrire. C'est ce qui rend l'ensemble homogène :
 * l'arrondi n'est plus une intention répétée dans dix tracés, c'est une
 * propriété du rendu.
 *
 * **Les détails restent tracés dans les deux états.** La porte d'une maison,
 * les lignes d'une page : remplies avec le corps, elles disparaîtraient dedans.
 */
function Icon({
  corps,
  details,
  filled,
}: {
  corps: string
  details?: string
  filled: boolean
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="size-[26px]"
      fill="none"
      stroke="currentColor"
      strokeWidth={filled ? 1.9 : 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={corps} fill={filled ? 'currentColor' : 'none'} />
      {details !== undefined && (
        // Les détails d'une icône PLEINE se dessinent dans la couleur du fond :
        // tracés en `currentColor` sur un corps rempli de la même couleur, ils
        // s'y noieraient et l'icône active deviendrait une tache.
        <path d={details} stroke={filled ? 'var(--fond)' : 'currentColor'} />
      )}
    </svg>
  )
}

/**
 * Fabrique l'icône d'une entrée à partir de sa silhouette.
 *
 * L'attribut n'est posé QUE s'il y a des détails : sous
 * `exactOptionalPropertyTypes`, passer `undefined` à une propriété facultative
 * n'est pas la même chose que ne pas la passer, et le compilateur a raison de
 * les distinguer.
 */
function icone(corps: string, details?: string): (filled: boolean) => ReactNode {
  return (filled) => <Icon corps={corps} filled={filled} {...(details === undefined ? {} : { details })} />
}

export const NAV: NavEntry[] = [
  {
    id: 'feed',
    label: 'Fil',
    primary: true,
    // Une maison dont le toit, les épaules et le pied s'arrondissent : les
    // trois jonctions qui étaient les plus sèches dans l'ancien tracé.
    icon: icone(
      'M12 3.2 3.8 10.1a2 2 0 0 0-.7 1.5v6.9a2.5 2.5 0 0 0 2.5 2.5h12.8a2.5 2.5 0 0 0 2.5-2.5v-6.9a2 2 0 0 0-.7-1.5z',
      'M9.6 21V16.4a2.4 2.4 0 0 1 4.8 0V21',
    ),
  },
  {
    id: 'themes',
    label: 'Thèmes',
    primary: true,
    icon: icone('M10.8 3.4a7.4 7.4 0 1 1 0 14.8 7.4 7.4 0 0 1 0-14.8z', 'M16.4 16.4 20.6 20.6'),
  },
  {
    id: 'courses',
    label: 'Formations',
    primary: true,
    // Un livre ouvert : deux volets qui se rejoignent sur une reliure. Les
    // coins portent tous le même rayon, y compris ceux du dos.
    icon: icone(
      'M3.2 5.6A1.6 1.6 0 0 1 4.8 4h3.4A3.8 3.8 0 0 1 12 6.7 3.8 3.8 0 0 1 15.8 4h3.4a1.6 1.6 0 0 1 1.6 1.6v10.8a1.6 1.6 0 0 1-1.6 1.6h-3.4A3.8 3.8 0 0 0 12 20.7a3.8 3.8 0 0 0-3.8-2.7H4.8a1.6 1.6 0 0 1-1.6-1.6z',
      'M12 6.7v14',
    ),
  },
  {
    // §146 — la banque est une DESTINATION, pas un onglet de Formations : on
    // n'y va pas pour lire une leçon mais pour chercher un exercice, et un
    // détour par le cours à chaque fois coûterait deux gestes sur mille.
    id: 'exercices',
    label: 'Exercices',
    primary: true,
    // Un crayon : on ne restitue pas ici, on résout — et l'on écrit pour
    // résoudre. Distinct du livre ouvert des Formations et de la boîte des
    // Archives, avec lesquels il partage la barre.
    icon: icone(
      'M4.6 19.4v-2.8L15.9 5.3a1.7 1.7 0 0 1 2.4 0l1.4 1.4a1.7 1.7 0 0 1 0 2.4L8.4 20.4H5.6a1 1 0 0 1-1-1z',
      'M14.4 6.8 18.2 10.6',
    ),
  },
  {
    id: 'archives',
    label: 'Archives',
    primary: true,
    // Une boîte d'archives : couvercle et corps d'un seul tenant, poignée
    // tracée. L'ancien dessin empilait deux rectangles à angles droits.
    icon: icone(
      'M4.4 4h15.2a1.4 1.4 0 0 1 1.4 1.4v2.2a1.4 1.4 0 0 1-1.4 1.4v9a2 2 0 0 1-2 2H6.4a2 2 0 0 1-2-2V9A1.4 1.4 0 0 1 3 7.6V5.4A1.4 1.4 0 0 1 4.4 4z',
      'M3.4 9h17.2M9.9 12.6h4.2',
    ),
  },
  {
    id: 'leaderboard',
    label: 'Classement',
    primary: true,
    icon: icone(
      'M7.6 3.4h8.8v5.2a4.4 4.4 0 0 1-8.8 0z',
      'M7.6 5.2H5.4v1.4a3.2 3.2 0 0 0 2.6 3.1M16.4 5.2h2.2v1.4a3.2 3.2 0 0 1-2.6 3.1M12 13v3.6M8.4 20.6h7.2',
    ),
  },
  {
    // 02 §F2.43 — les questions qu'un camarade vous envoie. Entrée SECONDAIRE :
    // on révise seul la plupart du temps, et la barre du bas ne compte que cinq
    // places (06 §5).
    id: 'amis',
    // RG-2.43 amendée — « Amis » ne décrivait que la moitié de l'écran :
    // suivre une chaîne de faculté n'est pas une amitié, et ranger les deux
    // sous le même mot ferait chercher les chaînes ailleurs.
    label: 'Mon Cercle',
    primary: false,
    icon: icone(
      'M9.3 3.6a3.4 3.4 0 1 1 0 6.8 3.4 3.4 0 0 1 0-6.8zM9.3 12.4c3.2 0 5.8 1.9 5.8 4.2v3.8H3.5v-3.8c0-2.3 2.6-4.2 5.8-4.2z',
      'M16.6 5.4a2.6 2.6 0 1 1 0 5.2M17.4 12.9c2.1.4 3.6 1.7 3.6 3.3v4.2h-3.3',
    ),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    primary: false,
    icon: icone(
      'M12 2.8a5.6 5.6 0 0 0-5.6 5.6c0 3.3-.75 5.3-1.5 6.45a.9.9 0 0 0 .75 1.4h12.7a.9.9 0 0 0 .75-1.4c-.75-1.15-1.5-3.15-1.5-6.45A5.6 5.6 0 0 0 12 2.8z',
      'M9.6 19.2a2.5 2.5 0 0 0 4.8 0',
    ),
  },
  {
    // RG-1.13, DECISIONS §53 — **en deux tapes au plus.** C'est le geste qui
    // décide de tout ce que le candidat verra ensuite : l'enfouir dans les
    // réglages reviendrait à cacher la commande principale du produit. Un
    // candidat inscrit à deux concours bascule de l'un à l'autre pendant sa
    // préparation — pas tous les jours, mais assez pour qu'un chemin enfoui
    // le décourage.
    id: 'concours',
    // §78 — « Concours préparé » devient « Principal ». Le mot était juste et
    // trop long : dans une feuille de six entrées, il faisait la seule ligne
    // qui se lit en deux temps.
    label: 'Principal',
    primary: false,
    // Une étoile dont les dix pointes s'arrondissent au lieu de piquer. Le
    // tracé reste une étoile ; c'est la plume à jointure ronde qui l'adoucit,
    // et c'est ce qui la fait tenir à côté des autres.
    icon: icone(
      'M12 3.3l2.6 5.3 5.8.85-4.2 4.1 1 5.8-5.2-2.75-5.2 2.75 1-5.8-4.2-4.1 5.8-.85z',
    ),
  },
  {
    // [DECISIONS §157, §165] — l'essai, l'abonnement, les paiements.
    //
    // **Une entrée à part, et pas un bloc du Compte.** Le compte porte des
    // réglages ; celui-ci porte un COMPTEUR — « il vous reste 41 h » — et un
    // achat. Rangé sous l'apparence et l'objectif quotidien, le candidat à qui
    // il reste six heures ne le trouverait pas au moment précis où il le
    // cherche.
    id: 'abonnement',
    label: 'Mon abonnement',
    primary: false,
    // Une horloge : ce qu'on vient regarder ici est du TEMPS, pas de l'argent.
    // Un billet ou une carte auraient dit « boutique » à quelqu'un qui vient
    // d'abord savoir combien il lui reste.
    icon: icone(
      'M12 3.4a8.6 8.6 0 1 1 0 17.2 8.6 8.6 0 0 1 0-17.2z',
      'M12 7.4V12l3.2 1.9',
    ),
  },
  {
    id: 'account',
    label: 'Compte',
    primary: false,
    icon: icone(
      'M12 3.6a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM12 13.4c4 0 7.2 2.3 7.2 5.1v1.9H4.8v-1.9c0-2.8 3.2-5.1 7.2-5.1z',
    ),
  },
]

/**
 * L'entrée du back-office — 02 §F7.1.
 *
 * **Séparée de `NAV`, et jamais `primary`.** Elle ne concerne qu'une poignée de
 * comptes, alors que la barre du bas n'a que cinq places réservées à ce que
 * TOUS les candidats font. La mettre dans la liste commune aurait demandé de la
 * filtrer partout ; l'en sortir la rend explicitement conditionnelle.
 *
 * Ce n'est PAS un contrôle d'accès : le serveur rend 404 sur les routes
 * éditoriales à qui n'y a pas droit. Cacher l'entrée évite seulement de
 * proposer une porte fermée.
 */
export const NAV_EDITORIAL: NavEntry = {
  id: 'editorial',
  label: 'Back-office',
  primary: false,
  // Un panneau aux quatre coins arrondis, et ses lignes. L'ancien dessin était
  // un rectangle à angles droits barré de trois traits — la forme la plus sèche
  // de toute la feuille.
  icon: icone(
    'M6.4 3.6h11.2a2.8 2.8 0 0 1 2.8 2.8v11.2a2.8 2.8 0 0 1-2.8 2.8H6.4a2.8 2.8 0 0 1-2.8-2.8V6.4a2.8 2.8 0 0 1 2.8-2.8z',
    'M7.8 8.6h8.4M7.8 12h8.4M7.8 15.4h5',
  ),
}

/** Les entrées que le rail de bureau met en avant — RG-D9. */
export const NAV_PRIMARY: NavEntry[] = NAV.filter((entry) => entry.primary)

/** Retrouve une entrée par identifiant, dans l'ordre demandé. */
function entrees(ids: string[]): NavEntry[] {
  return ids
    .map((id) => NAV.find((e) => e.id === id))
    .filter((e): e is NavEntry => e !== undefined)
}

/**
 * Les QUATRE destinations de la barre du bas — RG-D25, [DECISIONS §47].
 *
 * Deux à gauche du bouton central, deux à droite. Ce sont les quatre lieux où
 * l'on **travaille** : on y va souvent, et on y reste.
 *
 * **L'ordre est celui de la barre**, et non celui de `NAV` : le fixer par
 * identifiant est ce qui garantit que « Fil » reste en première position le
 * jour où une entrée s'ajoute au registre.
 */
export const NAV_BARRE: NavEntry[] = entrees(['feed', 'courses', 'exercices', 'archives'])

/**
 * Ce que le bouton central ouvre — RG-D25.
 *
 * Des **détours** : on les ouvre, on regarde, on revient. Les mettre au même
 * rang que les quatre destinations ferait huit cibles de 45 px sur un écran de
 * 360 — sous le seuil tactile — et diluerait les quatre qui comptent.
 *
 * « Thèmes » y figure bien qu'il soit mis en avant sur le rail de bureau : sur
 * petit écran, on compose ses thèmes une fois, puis on révise.
 */
export const NAV_FEUILLE: NavEntry[] = entrees([
  // RG-1.13 — « Principal » en tête : c'est la commande qui décide du
  // contenu de tout le reste, et non un réglage parmi d'autres.
  'concours',
  'themes',
  'amis',
  'notifications',
  'leaderboard',
  // [DECISIONS §157] — juste avant « Compte », et pas dedans. C'est le seul
  // détour de la feuille qui porte un compte à rebours : quelqu'un à qui il
  // reste six heures doit le trouver en deux tapes, pas en trois.
  'abonnement',
  'account',
])

/**
 * La navigation de l'ATELIER éditorial — RG-D9bis, [DECISIONS §115].
 *
 * **Le back-office est un espace, pas un onglet.** Il s'ouvrait avec la
 * navigation du candidat autour de lui : un relecteur qui traite mille cinq
 * cents clés avait sous les yeux « Fil », « Formations », « Archives » et
 * « Mon Cercle » — quatre destinations dont aucune ne sert son travail — et
 * devait chercher ses cinq postes dans une rangée d'onglets À L'INTÉRIEUR de
 * l'écran. La navigation principale était occupée par ce dont il n'a pas
 * besoin, et son plan de travail relégué au second rang.
 *
 * **L'ordre suit le geste, pas l'objet.** On ouvre l'atelier pour savoir quoi
 * faire (`En attente`), on le fait (`Établi`), on crée ce qui manque
 * (`Ajouter`), on ouvre les portes (`Publier`), on contrôle (`Vérifier`).
 * C'est la journée d'un éditeur dans l'ordre où elle se passe.
 *
 * **`Mesure` ferme la liste** — [DECISIONS §187]. Les cinq premiers postes sont
 * du travail sur le corpus ; celui-ci est une lecture sur les candidats. Il ne
 * s'ouvre pas entre deux relectures, il s'ouvre le lundi.
 *
 * **Même grammaire d'icônes que le candidat** : une silhouette, un seul tracé,
 * jointures rondes. Ce qui change est le contenu de la navigation, pas sa
 * forme — et c'est ce qui fait qu'on ne se sent pas dans une autre application.
 */
export const NAV_ATELIER: NavEntry[] = [
  {
    id: 'attente',
    label: 'En attente',
    primary: true,
    // Une pile de plateaux : ce qui s'empile en attendant un geste.
    icon: icone(
      'M3.6 15.4 12 19.6l8.4-4.2',
      'M3.6 11.2 12 15.4l8.4-4.2M12 4.4 3.6 8.6 12 12.8l8.4-4.2z',
    ),
  },
  {
    id: 'etabli',
    label: 'Établi',
    primary: true,
    // Un plan de travail et son pied : le poste où l'on traite une pièce.
    icon: icone(
      'M3.4 8.4h17.2a1.4 1.4 0 0 1 1.4 1.4v1.4a1.4 1.4 0 0 1-1.4 1.4H3.4A1.4 1.4 0 0 1 2 11.2V9.8a1.4 1.4 0 0 1 1.4-1.4z',
      'M5.6 12.6v7.8M18.4 12.6v7.8',
    ),
  },
  {
    id: 'ajouter',
    label: 'Ajouter',
    primary: true,
    // Le même plus que le bouton central de la barre, dans un cercle.
    icon: icone(
      'M12 3.4a8.6 8.6 0 1 1 0 17.2 8.6 8.6 0 0 1 0-17.2z',
      'M12 8.2v7.6M8.2 12h7.6',
    ),
  },
  {
    id: 'publier',
    label: 'Publier',
    primary: true,
    // Une flèche qui SORT — la même grammaire que « partager ».
    icon: icone(
      'M4.2 13.4v5.4a1.4 1.4 0 0 0 1.4 1.4h12.8a1.4 1.4 0 0 0 1.4-1.4v-5.4',
      'M12 3.4v11.4M8.2 7.2 12 3.4l3.8 3.8',
    ),
  },
  {
    id: 'verifier',
    label: 'Vérifier',
    primary: true,
    // Une loupe : on cherche ce qui ne va pas, on ne coche pas ce qui va.
    icon: icone(
      'M10.8 3.6a7.2 7.2 0 1 1 0 14.4 7.2 7.2 0 0 1 0-14.4z',
      'M16.2 16.2 20.6 20.6',
    ),
  },
  {
    /**
     * **Le sixième poste, et le seul qui ne parle pas du corpus** —
     * [DECISIONS §187].
     *
     * En DERNIER des postes, juste avant le retour : les cinq précédents sont
     * du travail, celui-ci est une lecture. On l'ouvre le matin ou le lundi,
     * pas entre deux relectures — le mettre en tête l'aurait mis sur le chemin
     * de gens qui viennent écrire des questions.
     *
     * **Il reste visible pour tous les rôles de l'atelier**, alors que le
     * serveur le réserve à l'administrateur. Le 404 du back-office existe pour
     * ne pas confirmer son existence à un CANDIDAT ; un éditeur, lui, est déjà
     * dedans, et lui cacher un poste demanderait de faire descendre le rôle
     * dans la coque entière — rail, barre du bas, en-tête — pour une porte que
     * le serveur garde de toute façon. L'écran dit « réservé à
     * l'administrateur » plutôt que d'afficher une panne.
     */
    id: 'mesure',
    label: 'Mesure',
    primary: true,
    // Une courbe qui monte dans un cadre ouvert : on mesure une évolution, on
    // ne pointe pas une valeur. Un seul tracé, comme les autres.
    icon: icone(
      'M3.6 3.6v15.4a1.4 1.4 0 0 0 1.4 1.4h15.4',
      'M7.4 15.4 11.2 10.6l3.2 2.8 4.2-6',
    ),
  },
  {
    /**
     * **Le septième poste** — [DECISIONS §258].
     *
     * Ni le corpus, ni les candidats : l'ÉQUIPE. C'est le seul poste qui agisse
     * sur d'autres personnes que soi, et le seul qui puisse fabriquer un
     * administrateur — d'où sa place tout en bas, après la lecture du lundi.
     *
     * **Visible pour tous les rôles de l'atelier, comme `Mesure`**, et pour la
     * même raison : le 404 du back-office existe pour ne pas confirmer son
     * existence à un CANDIDAT, pas pour jouer à cache-cache avec un éditeur qui
     * est déjà dedans. L'écran dit « réservé à l'administrateur » plutôt que
     * d'afficher une panne.
     */
    id: 'comptes',
    label: 'Comptes',
    primary: true,
    // Une silhouette et une clé : ce poste ne montre pas des gens, il montre
    // ce que des gens ont le droit de faire. Un seul tracé, comme les autres.
    icon: icone(
      'M9.4 3.8a3.6 3.6 0 1 1 0 7.2 3.6 3.6 0 0 1 0-7.2z',
      'M3.2 20.2a6.2 6.2 0 0 1 12.4 0M17.4 12.6a2.4 2.4 0 1 1 0 4.8 2.4 2.4 0 0 1 0-4.8zM19.6 16.4l1.8 1.8-1 1',
    ),
  },
  {
    /**
     * **Le retour est une ENTRÉE, pas une croix** — §115.
     *
     * En dernier, au même rang que les autres : c'est une destination, pas une
     * fermeture. Une croix aurait dit « vous êtes dans une fenêtre » ; on n'est
     * pas dans une fenêtre, on est dans l'autre moitié du produit.
     */
    id: 'front-office',
    label: 'Front-office',
    primary: false,
    // Une flèche qui RENTRE dans une porte — l'inverse exact de « Publier ».
    icon: icone(
      'M13.6 3.6h4.6a2 2 0 0 1 2 2v12.8a2 2 0 0 1-2 2h-4.6',
      'M3.6 12h10.2M10.2 8.2 14 12l-3.8 3.8',
    ),
  },
]
